import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  /** Auto-dismiss duration in ms (default 4000) */
  duration: number;
}

export interface ToastContextValue {
  addToast: (item: Omit<ToastItem, "id">) => void;
  removeToast: (id: string) => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

export const ToastContext = createContext<ToastContextValue | null>(null);

export function useToastContext(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>.");
  return ctx;
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function SuccessIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
      aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function DismissIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
      aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

// ── Variant style maps ────────────────────────────────────────────────────────

const ICON_EL: Record<ToastVariant, React.ReactNode> = {
  success: <SuccessIcon />,
  error:   <ErrorIcon />,
  warning: <WarningIcon />,
  info:    <InfoIcon />,
};

const ICON_COLOR: Record<ToastVariant, string> = {
  success: "text-green-500 dark:text-green-400",
  error:   "text-red-500 dark:text-red-400",
  warning: "text-amber-500 dark:text-amber-400",
  info:    "text-blue-500 dark:text-blue-400",
};

const BORDER_COLOR: Record<ToastVariant, string> = {
  success: "border-l-green-500 dark:border-l-green-400",
  error:   "border-l-red-500 dark:border-l-red-400",
  warning: "border-l-amber-500 dark:border-l-amber-400",
  info:    "border-l-blue-500 dark:border-l-blue-400",
};

const BAR_COLOR: Record<ToastVariant, string> = {
  success: "bg-green-500 dark:bg-green-400",
  error:   "bg-red-500 dark:bg-red-400",
  warning: "bg-amber-500 dark:bg-amber-400",
  info:    "bg-blue-500 dark:bg-blue-400",
};

// ── Individual toast item ─────────────────────────────────────────────────────

interface ToastItemProps {
  toast: ToastItem;
  onRemove: (id: string) => void;
}

function ToastCard({ toast, onRemove }: ToastItemProps) {
  const [visible, setVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const dismissedRef = useRef(false);

  const dismiss = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    setVisible(false);
    setTimeout(() => onRemove(toast.id), 300);
  }, [onRemove, toast.id]);

  // Enter animation
  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // Progress timer — pauses on hover
  useEffect(() => {
    if (isHovered || dismissedRef.current) return;
    const interval = setInterval(() => {
      setElapsed(e => e + 50);
    }, 50);
    return () => clearInterval(interval);
  }, [isHovered]);

  // Auto-dismiss when time elapses
  useEffect(() => {
    if (!dismissedRef.current && elapsed >= toast.duration) {
      dismiss();
    }
  }, [elapsed, toast.duration, dismiss]);

  const progress = Math.max(0, 100 - (elapsed / toast.duration) * 100);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        "relative w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden",
        "rounded-card border-l-4",
        "bg-surface dark:bg-surface-raised",
        "transition-all duration-300 ease-out",
        visible
          ? "translate-x-0 opacity-100"
          : "translate-x-full opacity-0",
        BORDER_COLOR[toast.variant],
      )}
      style={{ boxShadow: "var(--shadow-card-hover)" }}
    >
      {/* Content */}
      <div className="flex items-start gap-3 px-4 pt-3.5 pb-4">
        {/* Variant icon */}
        <span className={cn("shrink-0 mt-0.5", ICON_COLOR[toast.variant])}>
          {ICON_EL[toast.variant]}
        </span>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-content-primary leading-snug">
            {toast.title}
          </p>
          {toast.description && (
            <p className="mt-0.5 text-sm text-content-secondary leading-snug">
              {toast.description}
            </p>
          )}
        </div>

        {/* Dismiss button */}
        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={dismiss}
          className={cn(
            "shrink-0 p-1 rounded transition-colors duration-150",
            "text-content-tertiary hover:text-content-primary",
            "hover:bg-surface-overlay dark:hover:bg-surface-overlay",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-brand-purple focus-visible:ring-offset-1",
            "focus-visible:ring-offset-surface",
          )}
        >
          <DismissIcon />
        </button>
      </div>

      {/* Progress bar */}
      <div
        aria-hidden="true"
        className={cn("h-0.5 transition-none", BAR_COLOR[toast.variant])}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

// ── Toast container (rendered in portal) ─────────────────────────────────────

interface ToastContainerProps {
  toasts: ToastItem[];
  onRemove: (id: string) => void;
}

function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      aria-label="Notifications"
      className="fixed bottom-4 right-4 z-[1080] flex flex-col gap-2 pointer-events-none"
    >
      {toasts.map(t => (
        <div key={t.id} className="pointer-events-auto">
          <ToastCard toast={t} onRemove={onRemove} />
        </div>
      ))}
    </div>,
    document.body,
  );
}

// ── Provider ──────────────────────────────────────────────────────────────────

interface ToastProviderProps {
  children: React.ReactNode;
  /** Maximum number of toasts visible at once (default 4) */
  maxToasts?: number;
}

export function ToastProvider({ children, maxToasts = 4 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback(
    (item: Omit<ToastItem, "id">) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setToasts(prev => {
        const next = [...prev, { ...item, id }];
        // Keep only the most recent maxToasts
        return next.length > maxToasts ? next.slice(next.length - maxToasts) : next;
      });
    },
    [maxToasts],
  );

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

ToastProvider.displayName = "ToastProvider";
